import { useEffect, useState } from 'react';

export interface ClientXpBoostInput {
  bonusPercent: number;
  expiresAtUtc: string;
}

/** API may return ISO without Z (SQL UTC); treat as UTC so local TZ does not shift the instant. */
function parseExpiresAtUtcMs(iso: string): number {
  const s = iso.trim();
  const hasZone = /Z$/i.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
  return Date.parse(hasZone ? s : `${s}Z`);
}

/**
 * Uses expiry from the server once; hides the UI badge after that instant without polling the API.
 */
export function useClientXpBoostCountdown(boost: ClientXpBoostInput | null | undefined): {
  show: boolean;
  bonusPercent: number;
} {
  const [pastDeadline, setPastDeadline] = useState(() => {
    if (!boost || boost.bonusPercent <= 0) return true;
    const end = parseExpiresAtUtcMs(boost.expiresAtUtc);
    return !Number.isFinite(end) || end <= Date.now();
  });

  useEffect(() => {
    if (!boost || boost.bonusPercent <= 0) {
      setPastDeadline(true);
      return;
    }
    const end = parseExpiresAtUtcMs(boost.expiresAtUtc);
    if (!Number.isFinite(end)) {
      setPastDeadline(true);
      return;
    }
    const msLeft = end - Date.now();
    if (msLeft <= 0) {
      setPastDeadline(true);
      return;
    }
    setPastDeadline(false);
    const id = window.setTimeout(() => setPastDeadline(true), msLeft + 80);
    return () => window.clearTimeout(id);
  }, [boost?.bonusPercent, boost?.expiresAtUtc]);

  const show = !!boost && boost.bonusPercent > 0 && !pastDeadline;
  return { show, bonusPercent: boost?.bonusPercent ?? 0 };
}
